import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { CanvasDocument } from '../../contexts/CanvasPanelContext';

// CDN worker avoids webpack/Babel bundling the CJS worker as a chunk (require is not defined in workers)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// ── Helpers ──────────────────────────────────────────────────────────────────

const IMAGE_MIMES = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp',
]);
const IMAGE_EXTS = /\.(png|jpe?g|gif|svg|webp)(\?.*)?$/i;
const PDF_EXTS = /\.pdf(\?.*)?$/i;

function resolveContentType(doc: CanvasDocument): 'pdf' | 'image' | 'unknown' {
  const mime = doc.mimeType?.toLowerCase() ?? '';
  if (mime === 'application/pdf' || PDF_EXTS.test(doc.uri)) return 'pdf';
  if (IMAGE_MIMES.has(mime) || IMAGE_EXTS.test(doc.uri)) return 'image';
  return 'unknown';
}

// ── Sub-viewers ───────────────────────────────────────────────────────────────

function PdfViewer({ doc }: { doc: CanvasDocument }) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [page, setPage] = useState(doc.page ?? 1);

  useEffect(() => {
    setPage(doc.page ?? 1);
  }, [doc.page, doc.uri]);

  const onLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Page navigation */}
      {numPages !== null && numPages > 1 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.5,
          borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
        }}>
          <IconButton size="small" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
            ‹
          </IconButton>
          <Typography variant="caption" sx={{ userSelect: 'none' }}>
            Page {page} / {numPages}
          </Typography>
          <IconButton size="small" onClick={() => setPage(p => Math.min(numPages, p + 1))} disabled={page >= numPages}>
            ›
          </IconButton>
        </Box>
      )}

      {/* PDF render area */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', p: 1, bgcolor: '#525659' }}>
        <Document
          file={doc.uri}
          onLoadSuccess={onLoadSuccess}
          loading={<Typography sx={{ color: '#fff', p: 2 }}>Loading PDF…</Typography>}
          error={<Typography sx={{ color: '#ffcdd2', p: 2 }}>Failed to load PDF.</Typography>}
        >
          <Page
            pageNumber={page}
            renderTextLayer
            renderAnnotationLayer
            width={undefined}
          />
        </Document>
      </Box>
    </Box>
  );
}

function ImageViewer({ doc }: { doc: CanvasDocument }) {
  return (
    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <img
        src={doc.uri}
        alt={doc.title ?? 'Image'}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </Box>
  );
}


function UnknownViewer({ doc }: { doc: CanvasDocument }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 3 }}>
      <Typography variant="body2" color="text.secondary">
        This file type cannot be previewed inline.
      </Typography>
      <a href={doc.uri} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        Open in new tab <OpenInNewIcon fontSize="small" />
      </a>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface CanvasPanelProps {
  document: CanvasDocument | null;
  onClose(): void;
}

export default function CanvasPanel({ document: doc, onClose }: CanvasPanelProps) {
  if (!doc) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No document loaded.</Typography>
      </Box>
    );
  }

  const contentType = resolveContentType(doc);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 2, py: 1,
        borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, gap: 1, minHeight: 48,
      }}>
        <Typography
          variant="subtitle2"
          noWrap
          title={doc.title}
          sx={{ flex: 1, fontWeight: 600 }}
        >
          {doc.title ?? 'Document'}
        </Typography>

        <Tooltip title="Open in new tab">
          <IconButton size="small" component="a" href={doc.uri} target="_blank" rel="noopener noreferrer">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {contentType === 'pdf' && <PdfViewer doc={doc} />}
        {contentType === 'image' && <ImageViewer doc={doc} />}
        {contentType === 'unknown' && <UnknownViewer doc={doc} />}
      </Box>
    </Box>
  );
}
